import { Analytics } from '@cobliteam/analytics';
import { Location } from '@cobliteam/common-types';
import { AiRecommendationJourney } from '@cobliteam/hooks/src/useAiRecommendationsStore/useAiRecommendationsStore';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags/useFeatureFlags';
import useGeofenceRecommendations from '@cobliteam/hooks/src/useGeofenceRecommendations/useGeofenceRecommendations';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import { useHistory } from 'react-router-dom';
import { useFloatingAIChat } from '../store';
import AIIcon from '../ui/AIIcon';
import { getFloatingChatPrompt, resolveFloatingChatPrompt, shouldOpenFloatingChatForRecommendation } from './useAiRecommendationCtaLogic';

const CTA_COLOR = '#0234E1';

export const JOURNEY_LOCATIONS: Record<AiRecommendationJourney, string> = {
  'driver-registration': Location.DRIVER_MANAGEMENT,
  'setup-driver-association': Location.DRIVER_MANAGEMENT,
  'vehicle-groups': Location.VEHICLES_GROUPS,
  'setup-groups': Location.VEHICLES_GROUPS,
  installation: Location.ORDERS,
  checklists: `${Location.CHECKLIST_ALL}?create=true&template=daily_exit`,
  'fleet-policies': Location.ALERTS_TRIGGERS_CREATE,
  geofences: Location.GEOFENCES_CREATE,
  'wifi-configuration': Location.CONFIG_CAMERA_WIFI_CONFIGURATION,
  'cam-audio': Location.CONFIG_ALERTS,
  'sensibility-configuration': Location.CONFIG_EVENTS_CONFIGURATION,
  'user-profiles': Location.CONFIG_USERS,
};

/**
 * Returns journey IDs whose target page matches the given pathname.
 * Querystrings on `JOURNEY_LOCATIONS` values (e.g. checklists) are ignored.
 */
export const getJourneysForLocation = (pathname: string): AiRecommendationJourney[] => {
  const normalized = pathname.split('?')[0];
  return (Object.entries(JOURNEY_LOCATIONS) as [AiRecommendationJourney, string][]).filter(([, location]) => location.split('?')[0] === normalized).map(([journeyId]) => journeyId);
};

interface UseAiRecommendationCtaOptions {
  component: string;
  journeyId: AiRecommendationJourney;
  aiPrompt?: string;
  /** Label shown for the CTA. Defaults to 'Ir para a página'. */
  primaryLabel?: string;
  /** Action called when the CTA is clicked. Defaults to navigating to the journey page. */
  onPrimaryAction?: () => void;
  /** Label shown for the CTA. Defaults to 'Ir para a página'. */
  secondaryLabel?: string;
  /** Action called when the CTA is clicked. Defaults to navigating to the journey page. */
  onSecondaryAction?: () => void;
  /** When true, ignores the `aiChat` flag so non-installation journeys always use the page-navigation fallback. */
  disableAiChat?: boolean;
  /** When true, CTA always opens the FloatingAIChatPanel with `aiPrompt`, bypassing JOURNEY_LOCATIONS navigation. */
  forceFloatingChat?: boolean;
}

const useAiRecommendationCta = ({
  component,
  journeyId,
  aiPrompt,
  primaryLabel,
  onPrimaryAction,
  secondaryLabel,
  onSecondaryAction,
  disableAiChat = false,
  forceFloatingChat = false,
}: UseAiRecommendationCtaOptions) => {
  const history = useHistory();
  const { aiChat, aiRecommendations2, aiGroupRecommendations } = useFeatureFlags();
  const { t } = useTranslation('ai');
  const openFloatingAIChat = useFloatingAIChat((s) => s.open);

  const isInstallation = journeyId === AiRecommendationJourney.INSTALLATION;
  const isGeofences = journeyId === AiRecommendationJourney.GEOFENCES;
  // Deep-links to the groups page (which owns the generate query) instead of the floating chat, keeping the LLM-cost guardrail intact.
  const showGroupRecommendationsCta = journeyId === AiRecommendationJourney.VEHICLE_GROUPS && aiGroupRecommendations;

  const aiChatEnabled = aiChat && !disableAiChat;
  const floatingChatPrompt = getFloatingChatPrompt(journeyId, t);
  const isFloatingChatJourney = floatingChatPrompt !== undefined;
  const shouldOpenFloatingChat = shouldOpenFloatingChatForRecommendation({ forceFloatingChat, journeyId });
  const showAiCta = aiChatEnabled || isFloatingChatJourney || forceFloatingChat;

  const { data: geofenceRecommendations } = useGeofenceRecommendations({ enabled: isGeofences && aiRecommendations2 });
  const firstGeofenceRec = geofenceRecommendations?.[0];
  const hasGeofenceRecommendations = isGeofences && firstGeofenceRec;

  const navigateToJourneyPage = (queryParams?: string) => {
    const base = JOURNEY_LOCATIONS[journeyId];
    if (base) history.push(queryParams ? `${base}?${queryParams}` : base);
  };

  const navigateToAiChat = () => {
    history.push(`${Location.AI}?prompt=${encodeURIComponent(aiPrompt ?? t('cta_default_prompt'))}`);
  };

  const handleAnalyticsClick = () => {
    Analytics.track('Ai Recommendation call to action clicked', {
      component: component,
      journeyId: journeyId,
    });
  };

  const handleOpenFloatingChat = () => {
    const prompt = resolveFloatingChatPrompt({ aiPrompt, forceFloatingChat, journeyId, t });

    Analytics.track('AI - Floating Chat - Opened', { component: 'ai-recommendation-cta', journeyId, prompt });
    openFloatingAIChat(prompt || undefined);
  };

  let onCta = () => undefined;
  let ctaButton = <></>;
  let ctaLabel = '';

  if (primaryLabel && onPrimaryAction) {
    onCta = () => {
      handleAnalyticsClick();
      onPrimaryAction();
    };

    ctaLabel = primaryLabel;
    ctaButton = (
      <Button variant='contained' color='primary' onClick={onCta}>
        {ctaLabel}
      </Button>
    );
  } else if (secondaryLabel && onSecondaryAction) {
    onCta = () => {
      handleAnalyticsClick();
      onSecondaryAction();
    };

    ctaLabel = secondaryLabel;
    ctaButton = (
      <Button variant='contained' color='primary' onClick={onCta}>
        {ctaLabel}
      </Button>
    );
  } else if (isInstallation) {
    onCta = () => {
      handleAnalyticsClick();
      handleOpenFloatingChat();
    };

    ctaLabel = t('cta_schedule_with_ai');
    ctaButton = (
      <button
        type='button'
        onClick={onCta}
        className='flex items-center gap-2 rounded-xl px-4 py-2 font-semibold text-sm text-white transition-opacity hover:opacity-90'
        style={{ background: CTA_COLOR }}
      >
        <AIIcon size={22} sparklesPaint='white' />
        {ctaLabel}
      </button>
    );
  } else if (hasGeofenceRecommendations) {
    onCta = () => {
      handleAnalyticsClick();
      navigateToJourneyPage(`recommendationId=${firstGeofenceRec.id}`);
    };

    ctaLabel = t('cta_create_geofence');
    ctaButton = (
      <button
        type='button'
        onClick={onCta}
        className='flex items-center gap-2 rounded-xl px-4 py-2 font-semibold text-sm text-white transition-opacity hover:opacity-90'
        style={{ background: CTA_COLOR }}
      >
        <AIIcon size={22} sparklesPaint='white' />
        {ctaLabel}
      </button>
    );
  } else if (showGroupRecommendationsCta) {
    onCta = () => {
      handleAnalyticsClick();
      navigateToJourneyPage('openGroupRecommendations=1');
    };

    ctaLabel = t('cta_view_group_recommendations');
    ctaButton = (
      <button
        type='button'
        onClick={onCta}
        className='flex items-center gap-2 rounded-xl px-4 py-2 font-semibold text-sm text-white transition-opacity hover:opacity-90'
        style={{ background: CTA_COLOR }}
      >
        <AIIcon size={22} sparklesPaint='white' />
        {ctaLabel}
      </button>
    );
  } else if (showAiCta) {
    onCta = () => {
      handleAnalyticsClick();
      if (shouldOpenFloatingChat) {
        handleOpenFloatingChat();
        return;
      }

      navigateToAiChat();
    };

    ctaLabel = t('cta_help_with_ai');
    ctaButton = (
      <button
        type='button'
        onClick={onCta}
        className='flex items-center gap-2 rounded-xl px-4 py-2 font-semibold text-sm text-white transition-opacity hover:opacity-90'
        style={{ background: CTA_COLOR }}
      >
        <AIIcon size={22} sparklesPaint='white' />
        {ctaLabel}
      </button>
    );
  } else {
    onCta = () => {
      handleAnalyticsClick();
      navigateToJourneyPage();
    };

    ctaLabel = t('cta_go_to_page');
    ctaButton = (
      <Button variant='contained' color='primary' onClick={onCta}>
        {ctaLabel}
      </Button>
    );
  }

  return { ctaButton, onCta, ctaLabel };
};

export default useAiRecommendationCta;
